import React, { useState } from "react";
import * as R from "ramda";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import * as moment from "moment";
import styled from "styled-components";
import Spinner from "../ui/Spinner";
import Image from "../ui/Image";
import { getPhotoForUserId } from "../../utils/utils";
import BallShadow from "../../images/arsenal_shadow.png";
import ballImageDefault from "../../images/default_ball.png";
import { ArrowUpOutlined, ArrowDownOutlined } from "@ant-design/icons";
import ConfirmDelete from "../ui/ConfirmDelete";
import {
  BoxArrowUpRight,
  ClipboardCheckFill,
  PersonUp,
} from "react-bootstrap-icons";
import JobStatusBadge from "./JobStatusBadge";
import NotifyCustomerModal from "./NotifyCustomerModal";
import Notify from "../ui/Notify";
import feedbackTheme from "../../theme/feedback";

const { color, radius, spacing } = feedbackTheme;

const resolveStatus = (job) => {
  if (job.status === "complete" || job.status === "waiting_pickup") {
    return job.status;
  }
  if (moment(job.date_due).isSameOrBefore(moment(), "day")) {
    return moment(job.date_due).isSame(moment(), "day") ? "late_today" : "late";
  }
  return job.status;
};

const ListCard = styled.div`
  background: ${color.surface};
  border: 1px solid #e5e7eb;
  border-radius: ${radius.lg};
  box-shadow: 0 1px 3px rgba(16, 24, 64, 0.06);
  overflow: hidden;

  & a {
    text-decoration: none;
    color: inherit;
  }
`;

const HeaderRow = styled.div`
  display: grid;
  grid-template-columns: 1.5fr 1fr 1fr 1.5fr 1.5fr 2fr 0.8fr;
  background: ${color.primary};
  color: ${color.surface};
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  position: sticky;
  top: 0;
  z-index: 2;
`;

const HeaderCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 4px;
  padding: 12px 12px;
  cursor: ${(p) => (p.$sortable ? "pointer" : "default")};
  user-select: none;

  &:hover {
    background: ${(p) => (p.$sortable ? "rgba(255,255,255,0.08)" : "transparent")};
  }
`;

const DataRow = styled.div`
  display: grid;
  grid-template-columns: 1.5fr 1fr 1fr 1.5fr 1.5fr 2fr 0.8fr;
  border-bottom: 1px solid #eef0f5;
  background: ${color.surface};
  transition: background 120ms ease;

  &:hover {
    background: #f7f8fc;
  }

  &:last-child {
    border-bottom: none;
  }
`;

const DataCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding: 12px 12px;
  font-size: 13px;
  color: ${color.text};
  min-height: 56px;
  word-break: break-word;
  overflow-wrap: anywhere;
  line-height: 1.4;
`;

const NoWrapCell = styled(DataCell)`
  white-space: nowrap;
`;

const ActionsCell = styled(DataCell)`
  justify-content: flex-end;
  gap: 6px;
  padding-right: 12px;
`;

const IconActionBtn = styled.button`
  width: 36px;
  height: 36px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: 1px solid transparent;
  border-radius: ${radius.md};
  color: ${(p) => p.$variant === "complete" ? color.success : color.primary};
  cursor: pointer;
  transition: all 120ms ease;

  &:hover {
    background: ${(p) => p.$variant === "complete" ? color.successBg : color.surfaceAlt};
    border-color: ${(p) => p.$variant === "complete" ? color.success : color.primary};
  }

  svg {
    font-size: 16px;
  }
`;

const ClientLinkIcon = styled(Link)`
  display: inline-flex;
  align-items: center;
  margin-left: 6px;
  color: ${color.textMuted};

  &:hover {
    color: ${color.primary};
  }
`;

const SortIcon = styled.span`
  display: inline-flex;
  font-size: 11px;
`;

const CardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: ${spacing.md};
`;

const JobCard = styled.div`
  background: ${color.surface};
  border: 1px solid #e5e7eb;
  border-radius: ${radius.lg};
  padding: ${spacing.md};
  box-shadow: 0 1px 3px rgba(16, 24, 64, 0.06);
  transition: box-shadow 160ms ease, transform 160ms ease;
  display: flex;
  flex-direction: column;
  gap: ${spacing.sm};

  &:hover {
    box-shadow: 0 8px 22px rgba(16, 24, 64, 0.12);
    transform: translateY(-2px);
  }

  & a {
    text-decoration: none;
    color: inherit;
  }
`;

const CardHead = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${spacing.sm};
`;

const CardTitle = styled.div`
  font-size: 15px;
  font-weight: 700;
  color: ${color.text};
  line-height: 1.3;
`;

const CardSubtitle = styled.div`
  font-size: 12px;
  color: ${color.textMuted};
  margin-top: 2px;
`;

const CardBody = styled.div`
  display: flex;
  gap: ${spacing.sm};
  align-items: center;
`;

const BallThumb = styled.div`
  position: relative;
  width: 64px;
  height: 64px;
  flex-shrink: 0;
  display: flex;
  align-items: flex-end;
  justify-content: center;

  & img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
  }
`;

const ShadowImage = styled.div`
  position: absolute;
  bottom: -6px;
  left: 0;
  right: 0;
  background-image: url(${BallShadow});
  background-repeat: no-repeat;
  background-position: center center;
  height: 14px;
  background-size: 100% 14px;
  opacity: 0.7;
`;

const CardMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  min-width: 0;
  font-size: 12px;
  color: ${color.textMuted};

  & > div {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const CardServices = styled.div`
  font-size: 12px;
  color: ${color.text};
  padding-top: ${spacing.xs};
  border-top: 1px solid #eef0f5;
`;

const CardActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 6px;
`;

const EmptyState = styled.div`
  background: ${color.surface};
  border: 1px dashed ${color.border};
  border-radius: ${radius.lg};
  padding: ${spacing.xl};
  text-align: center;
  color: ${color.textMuted};
  font-size: 14px;
`;

export const JobBoardDashboardList = (props) => {
  const {
    jobs,
    loadingCustomer,
    bowlerID,
    ballID,
    plugin_settings,
    jobServices,
    statusFilter,
    locationFilter,
    staffFilter,
    isList,
    userId,
    jobHandleForm,
    jobNotifyCustomer,
    refreshJobBoardDashboard,
  } = props;

  const [showConfirmComplete, setShowConfirmComplete] = useState(false);
  const [currentJob, setCurrentJob] = useState(null);
  const [showConfirmPendingPickup, setShowConfirmPendingPickup] =
    useState(false);
  // Asked after the job has already moved (#855): whether the bowler hears
  // about it is a separate decision from the status change itself.
  const [jobToNotify, setJobToNotify] = useState(null);
  const { t } = useTranslation();
  const [sortedBy, setSortedBy] = useState({
    field: "date_due",
    direction: -1,
  });

  let jobsSorted = jobs
    .map((job) => {
      const selectedJobServices = R.map(
        R.path(["job_service", "id"]),
        job.job_details || []
      );

      const selectedServices = R.join(
        ", ",
        R.map(
          R.prop("name"),
          R.filter(
            (jobService) => R.contains(jobService.id, selectedJobServices),
            jobServices || []
          )
        )
      );

      const status =
        job.status !== "complete" && job.status !== "waiting_pickup"
          ? moment(job.date_due).isSameOrBefore(moment(), "day")
            ? "late"
            : job.status
          : job.status;

      return {
        ...job,
        selectedServices,
        status,
      };
    })
    .sort((a, b) =>
      R.path(sortedBy.field.split("."))(a) <=
      R.path(sortedBy.field.split("."))(b)
        ? sortedBy.direction * -1
        : sortedBy.direction
    );

  jobsSorted = !R.isEmpty(statusFilter)
    ? jobsSorted.filter((job) =>
        statusFilter.includes(job.status.toLowerCase())
      ) || []
    : jobsSorted;

  // console.log("status filter ->", statusFilter, jobsSorted);

  const getPhoto = getPhotoForUserId(userId);

  const toggleSort = (field) =>
    setSortedBy({
      field,
      direction: sortedBy.field !== field ? 1 : sortedBy.direction * -1,
    });

  const sortIndicator = (field) =>
    sortedBy.field === field ? (
      <SortIcon>
        {sortedBy.direction === 1 ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
      </SortIcon>
    ) : null;

  const renderActions = (job) => (
    <>
      {job.status !== "waiting_pickup" && job.status !== "complete" && (
        <IconActionBtn
          aria-label={t("jobBoard.pendingPickupJob")}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setCurrentJob(job);
            setShowConfirmPendingPickup(true);
          }}
        >
          <PersonUp />
        </IconActionBtn>
      )}
      {job.status !== "complete" && (
        <IconActionBtn
          $variant="complete"
          aria-label={t("jobBoard.completeJob")}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setCurrentJob(job);
            setShowConfirmComplete(true);
          }}
        >
          <ClipboardCheckFill />
        </IconActionBtn>
      )}
    </>
  );

  if (loadingCustomer && jobsSorted.length === 0) return <Spinner />;

  if (jobsSorted.length === 0) {
    return <EmptyState>{t("jobBoard.noResults") || "No jobs to show"}</EmptyState>;
  }

  if (isList) {
    return (
      <>
        <ListCard>
          <HeaderRow>
            <HeaderCell $sortable onClick={() => toggleSort("location")}>
              {t("jobBoard.location")}
              {sortIndicator("location")}
            </HeaderCell>
            <HeaderCell $sortable onClick={() => toggleSort("status")}>
              {t("jobBoard.status")}
              {sortIndicator("status")}
            </HeaderCell>
            <HeaderCell $sortable onClick={() => toggleSort("date_due")}>
              {t("jobBoard.due")}
              {sortIndicator("date_due")}
            </HeaderCell>
            <HeaderCell
              $sortable
              onClick={() => toggleSort("ball_info.customer")}
            >
              {t("jobBoard.client")}
              {sortIndicator("ball_info.customer")}
            </HeaderCell>
            <HeaderCell
              $sortable
              onClick={() => toggleSort("ball_info.ball_name")}
            >
              {t("jobBoard.ball")}
              {sortIndicator("ball_info.ball_name")}
            </HeaderCell>
            <HeaderCell
              $sortable
              onClick={() => toggleSort("selectedServices")}
            >
              {t("jobBoard.job")}
              {sortIndicator("selectedServices")}
            </HeaderCell>
            <HeaderCell style={{ justifyContent: "flex-end" }}>
              {t("jobBoard.complete")}
            </HeaderCell>
          </HeaderRow>
          {jobsSorted.map((job) => {
            const status = resolveStatus(job);
            return (
              <Link
                key={job.id}
                to={`/arsenal/${
                  job.ball_info ? job.ball_info.customer_id : ""
                }/ball/${
                  job.ball_id ? job.ball_id : ""
                }/job/${job.id.toString()}`}
              >
                <DataRow>
                  <DataCell>
                    {job?.job_location
                      ? job.job_location_name
                      : t("jobBoard.noLocation")}
                  </DataCell>
                  <NoWrapCell>
                    <JobStatusBadge status={status} />
                  </NoWrapCell>
                  <NoWrapCell>
                    {moment(job.date_due).format("MM/DD/YYYY")}
                  </NoWrapCell>
                  <DataCell>
                    <span>
                      {job.ball_info ? job.ball_info.customer : ""}
                    </span>
                    {job.ball_info && (
                      <ClientLinkIcon
                        to={`/bowler/${job.ball_info.customer_id}`}
                        onClick={(e) => e.stopPropagation()}
                        aria-label="Open bowler"
                      >
                        <BoxArrowUpRight size={14} />
                      </ClientLinkIcon>
                    )}
                  </DataCell>
                  <DataCell>
                    {job.ball_info ? job.ball_info.ball_name : ""}
                  </DataCell>
                  <DataCell>{job.selectedServices}</DataCell>
                  <ActionsCell>{renderActions(job)}</ActionsCell>
                </DataRow>
              </Link>
            );
          })}
        </ListCard>
        {renderConfirmDialogs()}
      </>
    );
  }

  return (
    <>
      <CardGrid>
        {jobsSorted.map((job) => {
          const status = resolveStatus(job);
          const image = getPhoto(job.ball_info?.photo);
          return (
            <JobCard key={job.id}>
              <Link
                to={`/arsenal/${
                  job.ball_info ? job.ball_info.customer_id : ""
                }/ball/${
                  job.ball_id ? job.ball_id : ""
                }/job/${job.id.toString()}`}
              >
                <CardHead>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <CardTitle>
                      {job.ball_info ? job.ball_info.customer : ""}
                    </CardTitle>
                    <CardSubtitle>
                      {moment(
                        job.date_completed ? job.date_completed : job.date_due
                      ).format("MM/DD/YYYY")}
                    </CardSubtitle>
                  </div>
                  <JobStatusBadge status={status} />
                </CardHead>
                <CardBody>
                  <BallThumb>
                    <Image
                      src={image === null ? ballImageDefault : image}
                      fluid="true"
                      alt=""
                      placeholderSrc={ballImageDefault}
                      wrapperProps={{
                        style: { width: "100%", height: "100%" },
                      }}
                    />
                    <ShadowImage />
                  </BallThumb>
                  <CardMeta>
                    <div>
                      {job.ball_info
                        ? `${job.ball_info.manufacturer || ""} ${
                            job.ball_info.ball_name || ""
                          }`.trim()
                        : ""}
                    </div>
                    {job.ball_info?.serial_number && (
                      <div>{job.ball_info.serial_number}</div>
                    )}
                    <div>
                      {job?.job_location
                        ? job.job_location_name
                        : t("jobBoard.noLocation")}
                    </div>
                  </CardMeta>
                </CardBody>
                {job.selectedServices && (
                  <CardServices>{job.selectedServices}</CardServices>
                )}
              </Link>
              <CardActions>{renderActions(job)}</CardActions>
            </JobCard>
          );
        })}
      </CardGrid>
      {renderConfirmDialogs()}
    </>
  );

  function renderConfirmDialogs() {
    return (
      <>
        <ConfirmDelete
          show={showConfirmPendingPickup}
          confirmDescription={"jobBoard.confirmPendingPickupJob"}
          confirmButtonText={"jobBoard.pendingPickupJob"}
          onHide={() => setShowConfirmPendingPickup(false)}
          onConfirmClick={() => {
            const movedJob = currentJob;
            jobHandleForm({
              job: {
                ...movedJob,
                date_completed: moment(),
                status: "waiting_pickup",
              },
            });
            setShowConfirmPendingPickup(false);
            setCurrentJob(null);
            refreshJobBoardDashboard();
            Notify.success(t("jobBoard.confirmPendingPickupJob"));
            setJobToNotify(movedJob);
          }}
        />
        <NotifyCustomerModal
          show={!!jobToNotify}
          customerName={jobToNotify?.ball_info?.customer}
          ballName={jobToNotify?.ball_info?.ball_name}
          onHide={() => setJobToNotify(null)}
          onSkip={() => setJobToNotify(null)}
          onSend={(note) => {
            jobNotifyCustomer({ jobId: jobToNotify.id, extraNote: note });
            setJobToNotify(null);
          }}
        />
        <ConfirmDelete
          show={showConfirmComplete}
          confirmDescription={"jobBoard.confirmCompleteJob"}
          confirmButtonText={"jobBoard.completeJob"}
          onHide={() => setShowConfirmComplete(false)}
          onConfirmClick={() => {
            jobHandleForm({
              job: {
                ...currentJob,
                date_completed: moment(),
                status: "complete",
              },
            });
            setShowConfirmComplete(false);
            setCurrentJob(null);
            refreshJobBoardDashboard();
            Notify.success(t("jobBoard.confirmCompleteJob"));
          }}
        />
      </>
    );
  }
};
